import type { AxiosError } from 'axios';
import {
  Center,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  Title,
  UnstyledButton
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { PhysicalObject, Space } from '@/api/generated/models';
import type { InfoField } from '@/components/common';
import {
  getGetObjectQueryKey,
  getListObjectsQueryKey,
  getSearchObjectsQueryKey,
  useActivateObject,
  useDeactivateObject,
  useGetObject
} from '@/api/generated/objects/objects';
import {
  getListSpacesQueryKey,
  useDeleteSpace,
  useListSpaces
} from '@/api/generated/work-structure/work-structure';
import { IconFigmaInfo } from '@/assets/icons';
import {
  AddActionButton,
  BackButton,
  InfoCard,
  StatusBanner,
  StatusPill
} from '@/components/common';
import { DeactivateObjectModal } from '@/features/price-lists/components/DeactivateObjectModal';
import { ObjectFormModal } from '@/features/price-lists/components/ObjectFormModal';
import { SpaceFormModal } from '@/features/price-lists/components/SpaceFormModal';
import { SpaceTree } from '@/features/price-lists/components/SpaceTree';
import { formatObjectType, objectStatusConfig } from '@/features/price-lists/objectDisplay';
import { successNotificationIcon } from '@/theme/notificationIcons';
import { formatDate } from '@/utils/format';

const spaceErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

/** GET /api/v1/objects/{id} vraća proširen oblik (vidi backend ObjectDetail) —
 * uz osnovna polja i brojače za fact-card statistiku. Springdoc ne zna
 * deklarirati taj oblik jer kontroler vraća ResponseEntity<?> (isti obrazac
 * kao PriceListController), pa se ovdje ručno tipizira. */
interface ObjectDetail extends PhysicalObject {
  linkedContractsCount?: number;
  weeklyReportsCount?: number;
}

export const ObjectDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading } = useGetObject(id ?? '', {
    query: { enabled: !!id, refetchOnMount: 'always' }
  });
  const object = data as ObjectDetail | undefined;

  const [editModalOpened, setEditModalOpened] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [deactivateModalOpened, setDeactivateModalOpened] = useState(false);
  const [addSpaceParentId, setAddSpaceParentId] = useState<string | null>(null);
  const [addSpaceModalOpened, setAddSpaceModalOpened] = useState(false);
  const [editingSpace, setEditingSpace] = useState<Space | null>(null);

  const { data: spacesData } = useListSpaces(id ?? '', { query: { enabled: !!id } });
  const spaces = spacesData ?? [];

  const deleteSpaceMutation = useDeleteSpace({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListSpacesQueryKey(id ?? '') });
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('spaceTree.deleteSuccess')
        });
      },
      onError: (error) => {
        notifications.show({
          color: 'red',
          message: spaceErrorMessage(error, t('common.error'))
        });
      }
    }
  });

  const deactivateMutation = useDeactivateObject({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetObjectQueryKey(id ?? '') });
        queryClient.invalidateQueries({ queryKey: getListObjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getSearchObjectsQueryKey() });
        setSavedMessage(t('objectRegistry.deactivateSuccess'));
        setDeactivateModalOpened(false);
      },
      onError: (error) => {
        notifications.show({
          color: 'red',
          message: spaceErrorMessage(error, t('common.error'))
        });
      }
    }
  });

  const activateMutation = useActivateObject({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetObjectQueryKey(id ?? '') });
        queryClient.invalidateQueries({ queryKey: getListObjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getSearchObjectsQueryKey() });
        setSavedMessage(t('objectRegistry.activateSuccess'));
      }
    }
  });

  if (isLoading) {
    return (
      <Center py={80}>
        <Loader color="var(--color-accent-primary)" />
      </Center>
    );
  }

  if (!object) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('objectRegistry.detailNotAvailable')}
        </Text>
      </Container>
    );
  }

  const statusConfig = object.isActive ? objectStatusConfig.active : objectStatusConfig.inactive;
  const statusLabel = t(statusConfig.labelKey);
  const typeLabel = formatObjectType(t, object.objectType);
  // Objekt je automatski kloniran iz ugovora druge organizacije (ContractService.send(), vidi
  // PhysicalObjectService.cloneForOrg) — read-only, izvor istine ostaje izvorna organizacija.
  const isReadOnly = !!object.clonedFromOrgId;

  const infoFields: InfoField[] = [
    { label: t('objectRegistry.colCode'), value: object.objectCode ?? '—' },
    { label: t('objectRegistry.fieldName'), value: object.objectName ?? '—' },
    { label: t('objectRegistry.colType'), value: typeLabel },
    { label: t('objectRegistry.colStatus'), value: statusLabel },
    { label: t('objectRegistry.colLocation'), value: object.location || '—' },
    { label: t('objectRegistry.colExternalRef'), value: object.externalReference || '—' },
    {
      label: t('objectRegistry.colLinkedContracts'),
      value: String(object.linkedContractsCount ?? 0)
    },
    {
      label: t('objectRegistry.colWeeklyReports'),
      value: String(object.weeklyReportsCount ?? 0)
    },
    { label: t('objectRegistry.createdLabel'), value: formatDate(object.createdAt) }
  ];

  const actionButtons = (
    <>
      {object.isActive ? (
        <UnstyledButton
          onClick={() => setDeactivateModalOpened(true)}
          style={{
            padding: '12px 20px',
            textAlign: 'center',
            borderRadius: 6,
            border: '1px solid var(--color-status-danger)',
            color: 'var(--color-status-danger)',
            fontSize: 14,
            fontWeight: 600
          }}
        >
          {t('objectRegistry.deactivateTitle')}
        </UnstyledButton>
      ) : (
        <UnstyledButton
          onClick={() => id && activateMutation.mutate({ id })}
          disabled={activateMutation.isPending}
          style={{
            padding: '12px 20px',
            textAlign: 'center',
            borderRadius: 6,
            backgroundColor: 'var(--color-accent-primary)',
            color: 'var(--color-text-on-accent)',
            fontSize: 14,
            fontWeight: 600,
            opacity: activateMutation.isPending ? 0.6 : 1
          }}
        >
          {t('objectRegistry.activateTitle')}
        </UnstyledButton>
      )}
      {!isReadOnly && (
        <UnstyledButton
          onClick={() => setEditModalOpened(true)}
          style={{
            padding: '12px 20px',
            textAlign: 'center',
            borderRadius: 6,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-surface)',
            color: 'var(--color-text-primary)',
            fontSize: 14,
            fontWeight: 600
          }}
        >
          {t('objectRegistry.editObject')}
        </UnstyledButton>
      )}
    </>
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/templates/object-registry" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        {isReadOnly && (
          <StatusBanner
            tone="info"
            icon={<IconFigmaInfo size={16} />}
            message={t('objectRegistry.readOnlyClone')}
          />
        )}

        <Group justify="space-between" wrap="nowrap" align="flex-start" visibleFrom="sm">
          <Group gap={12}>
            <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
              {object.objectCode}
            </Title>
            <StatusPill header label={statusLabel} bg={statusConfig.bg} fg={statusConfig.fg} />
          </Group>
          <Group gap={12} wrap="nowrap">
            {actionButtons}
          </Group>
        </Group>
        <Stack gap={12} hiddenFrom="sm">
          <Stack gap={8}>
            <Title order={2} fz={22} style={{ color: 'var(--color-text-primary)' }}>
              {object.objectCode}
            </Title>
            <StatusPill header label={statusLabel} bg={statusConfig.bg} fg={statusConfig.fg} />
          </Stack>
          <Group gap={12} grow wrap="nowrap">
            {actionButtons}
          </Group>
        </Stack>

        <InfoCard
          title={t('objectRegistry.basicInfoTitle')}
          fields={infoFields}
          columns={4}
          borderColor="var(--color-border-default)"
          trailing={
            <Stack gap={4}>
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {t('objectRegistry.fieldDescription')}
              </Text>
              <Text fz={13.5} style={{ color: 'var(--color-text-secondary)' }}>
                {object.description || t('objectRegistry.noDescription')}
              </Text>
            </Stack>
          }
        />

        <Stack gap={12}>
          <Group justify="space-between" align="center">
            <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {t('spaceTree.title')}
            </Text>
            {!isReadOnly && (
              <AddActionButton
                onClick={() => {
                  setAddSpaceParentId(null);
                  setAddSpaceModalOpened(true);
                }}
              >
                {t('spaceTree.addRootButton')}
              </AddActionButton>
            )}
          </Group>
          <SpaceTree
            objectName={`${object.objectCode} — ${object.objectName}`}
            spaces={spaces}
            onAddChild={(parentSpaceId) => {
              setAddSpaceParentId(parentSpaceId);
              setAddSpaceModalOpened(true);
            }}
            onDelete={(spaceId) => deleteSpaceMutation.mutate({ id: spaceId })}
            onEdit={(space) => setEditingSpace(space)}
            readOnly={isReadOnly}
          />
        </Stack>
      </Stack>

      <ObjectFormModal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        object={object}
        onSaved={setSavedMessage}
      />

      <DeactivateObjectModal
        opened={deactivateModalOpened}
        objectName={object.objectName ?? ''}
        onClose={() => setDeactivateModalOpened(false)}
        onConfirm={() => id && deactivateMutation.mutate({ id })}
        loading={deactivateMutation.isPending}
        isPaused={deactivateMutation.isPaused}
      />

      <SpaceFormModal
        opened={addSpaceModalOpened}
        onClose={() => setAddSpaceModalOpened(false)}
        objectId={id ?? ''}
        parentSpaceId={addSpaceParentId}
        parentLabel={
          addSpaceParentId
            ? (spaces.find((s) => s.id === addSpaceParentId)?.name ?? '—')
            : `${object.objectCode} — ${object.objectName}`
        }
        existingSpaces={spaces}
        onSaved={setSavedMessage}
      />

      <SpaceFormModal
        opened={!!editingSpace}
        onClose={() => setEditingSpace(null)}
        objectId={id ?? ''}
        parentSpaceId={editingSpace?.parentSpaceId ?? null}
        parentLabel={
          editingSpace?.parentSpaceId
            ? (spaces.find((s) => s.id === editingSpace.parentSpaceId)?.name ?? '—')
            : `${object.objectCode} — ${object.objectName}`
        }
        space={editingSpace ?? undefined}
        existingSpaces={spaces}
        onSaved={setSavedMessage}
      />
    </Container>
  );
};
